import React from "react";
import { SEOHead } from "@/components/SEOHead";
import { Footer } from "@/components/Footer";

export default function Terms() {
  return (
    <>
      <SEOHead
        title="Terms of Service | DayOne"
        description="DayOne terms of service — acceptable use, ownership, IP, and subscription terms."
      />
      <div className="min-h-screen lavender-bg" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
        <div className="max-w-[800px] mx-auto px-6 pt-16 pb-24">
          {/* Header */}
          <p className="text-xs font-medium tracking-widest uppercase mb-4" style={{ color: "#7c6af0" }}>
            Terms of Service
          </p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif", color: "#18151f" }}>
            Terms of Service
          </h1>
          <p className="text-sm mb-1" style={{ color: "#6b5f8a" }}>
            DayOne — usedayone.net
          </p>
          <p className="text-sm mb-12" style={{ color: "#6b5f8a" }}>
            Effective Date: April 24, 2026 | Last Updated: April 24, 2026
          </p>

          <div className="space-y-10 text-[15px] leading-[1.75]" style={{ color: "#6b5f8a" }}>
            {/* 1 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>1. Agreement to Terms</h2>
              <p>
                By accessing or using DayOne ("Service") at usedayone.net, you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service. DayOne is operated by Stone Sargent, based in Florence, Italy.
              </p>
            </section>

            {/* 2 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>2. Description of Service</h2>
              <p className="mb-3">DayOne is an AI-powered platform that provides:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>A curated library of validated startup ideas with business plans and market analysis</li>
                <li>An AI application builder that generates React web applications from natural language prompts</li>
                <li>Tools to edit, preview, and publish generated applications to Vercel</li>
                <li>A personalized idea recommendation engine</li>
              </ul>
              <p className="mt-3">
                The Service is provided "as is" and may be updated, modified, or discontinued at any time.
              </p>
            </section>

            {/* 3 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>3. Eligibility</h2>
              <p>
                You must be at least 16 years old to use DayOne. By using the Service, you represent that you meet this requirement.
              </p>
            </section>

            {/* 4 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>4. Account Registration</h2>
              <p className="mb-3">To access most features, you must create an account. You agree to:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Provide accurate and complete information during registration</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access at <a href="mailto:support@usedayone.net" className="underline" style={{ color: "#7c6af0" }}>support@usedayone.net</a></li>
                <li>Accept responsibility for all activity that occurs under your account</li>
              </ul>
            </section>

            {/* 5 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>5. Acceptable Use</h2>

              <h3 className="text-sm font-semibold mt-4 mb-2" style={{ color: "#18151f" }}>You May:</h3>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Use DayOne to build applications for personal or commercial purposes</li>
                <li>Export and use the code generated by DayOne</li>
                <li>Share your published applications</li>
                <li>Access and use the startup idea library for personal research and business planning</li>
              </ul>

              <h3 className="text-sm font-semibold mt-4 mb-2" style={{ color: "#18151f" }}>You May Not:</h3>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Use DayOne to generate applications that violate applicable laws</li>
                <li>Build applications designed to harm, defraud, or deceive others</li>
                <li>Attempt to reverse engineer, scrape, or extract our AI models or idea database</li>
                <li>Use the Service to generate spam, malware, or harmful content</li>
                <li>Impersonate other individuals or entities</li>
                <li>Circumvent any rate limits, security measures, or access controls</li>
                <li>Resell or sublicense access to DayOne without our written permission</li>
                <li>Use DayOne to build a competing AI app builder product</li>
              </ul>
            </section>

            {/* 6 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>6. Intellectual Property</h2>
              <p className="mb-3">
                <strong style={{ color: "#18151f" }}>Your Content:</strong> You retain ownership of the content you input into DayOne and the applications you generate. By using DayOne, you grant us a limited, non-exclusive license to process your inputs to provide the Service.
              </p>
              <p className="mb-3">
                <strong style={{ color: "#18151f" }}>DayOne Content:</strong> The DayOne platform, including its design, codebase, idea database, business plans, and AI systems, is our intellectual property. You may not copy, reproduce, or distribute DayOne's proprietary content without written permission.
              </p>
              <p>
                <strong style={{ color: "#18151f" }}>AI-Generated Code:</strong> Code generated by DayOne's AI builder is provided for your use. We do not claim ownership of generated code. We make no warranty that generated code is free from third-party intellectual property claims.
              </p>
            </section>

            {/* 7 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>7. Startup Ideas and Business Plans</h2>
              <p>
                The startup ideas, business plans, and market analysis in DayOne's idea library are for informational purposes only. They do not constitute professional business, legal, or financial advice. We make no guarantee of the accuracy or commercial viability of any idea. Conduct your own due diligence before pursuing any business idea.
              </p>
            </section>

            {/* 8 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>8. AI-Generated Content Disclaimer</h2>
              <p className="mb-3">You acknowledge that:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>AI-generated code may contain errors, bugs, or security vulnerabilities</li>
                <li>Generated applications are prototypes and may require refinement for production use</li>
                <li>DayOne is not responsible for the quality or security of generated applications</li>
                <li>You are responsible for reviewing and testing any code before deploying it in production</li>
              </ul>
            </section>

            {/* 9 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>9. Third-Party Services</h2>
              <p>
                DayOne integrates with Vercel, Supabase, Google, Anthropic, and Replicate. Your use of these services is subject to their respective terms. We are not responsible for the availability or actions of third-party services.
              </p>
            </section>

            {/* 10 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>10. Fees and Payment</h2>
              <p>
                Some features require a paid subscription. Current pricing is listed on the Pricing page at /pricing. We may change pricing with 30 days notice to existing subscribers. Subscriptions renew automatically until cancelled. You may cancel anytime by contacting support@usedayone.net — after cancelling, you retain access through the end of your current billing period, and we will not charge you again. All fees are non-refundable except where required by law.
              </p>
            </section>

            {/* 11 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>11. Termination</h2>
              <p>
                You may terminate your account by contacting{" "}
                <a href="mailto:support@usedayone.net" className="underline" style={{ color: "#7c6af0" }}>support@usedayone.net</a>.
                We may suspend or terminate your access immediately if you violate these Terms. Upon termination, your right to use the Service ceases.
              </p>
            </section>

            {/* 12 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>12. Disclaimers</h2>
              <p style={{ color: "#18151f", fontWeight: 600 }}>
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
            </section>

            {/* 13 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>13. Limitation of Liability</h2>
              <p style={{ color: "#18151f", fontWeight: 600 }}>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, DAYONE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM, OR $100, WHICHEVER IS GREATER.
              </p>
            </section>

            {/* 14 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>14. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless DayOne and its founder from any claims, losses, damages, or expenses arising from your use of the Service or violation of these Terms.
              </p>
            </section>

            {/* 15 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>15. Governing Law</h2>
              <p>
                These Terms are governed by the laws of the State of Texas, United States, without regard to conflict of law provisions. Any disputes will be resolved in the state or federal courts of Dallas County, Texas. EU residents retain their non-waivable consumer protection rights under local law.
              </p>
            </section>

            {/* 16 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>16. Changes to Terms</h2>
              <p>
                We may update these Terms at any time with 14 days notice for material changes. Continued use constitutes acceptance.
              </p>
            </section>

            {/* 17 */}
            <section>
              <h2 className="text-lg font-semibold mb-3" style={{ color: "#7c6af0" }}>17. Contact</h2>
              <p>
                DayOne |{" "}
                <a href="mailto:support@usedayone.net" className="underline" style={{ color: "#7c6af0" }}>support@usedayone.net</a>{" "}
                | usedayone.net
              </p>
              <p className="mt-1" style={{ color: "#71717a" }}>
                Stone Sargent, Founder | Florence, Italy
              </p>
            </section>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
